import { defineStore } from 'pinia'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast/use-toast'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useAppStore } from '@/stores/app'

export interface UserSettings {
  timeFormat: string
  language: string
  themeMode: string
  drawer: boolean
  mini: boolean
  tz: string | null
  location: { lat: number | null, lng: number | null }
  projects: { filtersShown: boolean, listView: boolean }
}

interface AuthParams {
  firebaseToken: string
  inviteCode?: string | null
  inviteDetails?: any
  inviteType?: string | null
  joinToken?: string | null
}

export const useUserStore = defineStore('user', {
  state: () => ({
    // Firebase user
    info: null as any,
    alerts: [] as any[],
    // The signed-in platform member
    member: {} as any,
    settings: {
      timeFormat: '24',
      language: 'english',
      themeMode: 'system',
      drawer: true,
      mini: false,
      tz: null,
      location: {
        lat: null,
        lng: null,
      },
      projects: {
        filtersShown: false,
        listView: true
      }
    } as UserSettings,
  }),

  actions: {
    setUser(user: any) {
      this.info = user
    },

    setMember(member: any) {
      this.member = member
    },

    setLanguage(language: string) {
      this.settings.language = language
    },

    setMainMenuDrawer(mini: boolean) {
      this.settings.mini = mini
    },

    changeTheme(mode: string) {
      this.settings.themeMode = mode
    },

    setProjectsFilters(view: boolean) {
      this.settings.projects.filtersShown = view
    },

    async updateAvatar(payload: { file: any }) {
      try {
        const response = await API({
          headers: {'content-type': 'multipart/form-data'},
          method: 'PATCH',
          url: `${HOST}/member/update-avatar`,
          data: {
            file: payload.file
          }
        })

        this.member = response.data

        toast({ title: 'Avatar updated' })
      } catch (e) {
        throw new Error("Can't update image. " + e)
      }
    },

    async update(payload: { displayName?: string, first_name?: string, last_name?: string }) {
      try {
        const response = await API({
          method: 'PATCH',
          url: `${HOST}/member/update`,
          data: {
            display_name: payload.displayName,
            first_name: payload.first_name,
            last_name: payload.last_name
          }
        })

        this.member = response.data
        toast({ title: 'User profile updated' })
      } catch (e) {
        throw new Error("Can't update member. " + e)
      }
    },

    setupSettings() {
      const storageSettings = localStorage.getItem('settings')
      if (storageSettings) {
        const parsed = JSON.parse(storageSettings)
        // Migrate old themeDark boolean to themeMode string
        if ('themeDark' in parsed && !('themeMode' in parsed)) {
          parsed.themeMode = parsed.themeDark ? 'dark' : 'light'
          delete parsed.themeDark
          localStorage.setItem('settings', JSON.stringify(parsed))
        }
        this.settings = parsed
      } else {
        if (Intl) {
          this.settings.tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      }
    },

    saveSettings() {
      localStorage.setItem('settings', JSON.stringify(this.settings))
    },

    updateLang(payload: { language: string }) {
      this.settings.language = payload.language
    },

    updateTz(payload: { tz: string | null }) {
      this.settings.tz = payload.tz
    },

    updateTimeFormat(payload: { timeFormat: string }) {
      this.settings.timeFormat = payload.timeFormat
    },

    // Backend session (JWT + member) for a signed-in Firebase user.
    async startSession(firebaseUser: any) {
      this.setupSettings()
      this.setUser(firebaseUser)
      const firebaseToken = await firebaseUser.getIdToken(true)
      useAppStore().setFirebaseToken(firebaseToken)
      return this.handleAuthentication({ firebaseToken })
    },

    async handleAuthentication({ firebaseToken, inviteCode = null, inviteDetails = null, inviteType = null, joinToken = null }: AuthParams) {
      try {
        const tokens = await this.getAuthTokens({ firebaseToken, inviteCode, inviteDetails, inviteType, joinToken })
        if (!tokens) {
          // User not registered in backend
          return { success: false, error: 'USER_NOT_REGISTERED', reason: 'user_not_registered' }
        }

        // Check if email is not verified
        if (tokens.emailNotVerified) {
          return { success: false, error: 'EMAIL_NOT_VERIFIED', reason: 'email_not_verified', message: tokens.message }
        }

        localStorage.setItem('accessToken', tokens.access_token)
        localStorage.setItem('refreshToken', tokens.refresh_token)

        const response = await API({
          method: 'GET',
          url: `${HOST}/member`
        })

        const member = response.data
        if (!member) {
          return { success: false, reason: 'member_not_found' }
        }

        this.member = member
        useAppStore().setIsLoggedIn(true)

        const sentry = (window as any).Sentry
        if (sentry && member.email) {
          sentry.setUser({ email: member.email })
        }

        await this.loadInitialData()

        return { success: true, member }
      } catch (error) {
        return { success: false, reason: 'error', error }
      }
    },

    // Exchange the stored refresh token for a fresh platform access token.
    // Used by the WebSocket client when the server rejects its token (close 4001):
    // the socket must not replay a token the server has already refused, and the
    // token it holds may be a Firebase ID token rather than a platform one, which
    // fails signature verification server-side.
    // Returns the new access token, or null if refresh is not possible — in which
    // case the caller should stop retrying and let the normal auth flow re-run.
    async refreshAccessToken() {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) return null

      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/token/refresh/`,
          data: { refresh_token: refreshToken }
        })

        const { access_token, refresh_token } = response.data || {}
        if (!access_token) return null

        localStorage.setItem('accessToken', access_token)
        if (refresh_token) localStorage.setItem('refreshToken', refresh_token)
        return access_token as string
      } catch (e) {
        // A refused refresh token means the session is genuinely gone; the Firebase
        // auth listener in App.vue is what recovers from that, not the socket.
        return null
      }
    },

    async getAuthTokens({ firebaseToken, inviteCode = null, inviteDetails = null, inviteType = null, joinToken = null }: AuthParams): Promise<any> {
      try {
        let response

        if (joinToken && inviteDetails) {
          // A join link is a sign-up credential like an invite; the backend registers and joins in one step.
          response = await API({
            method: 'POST',
            url: `${HOST}/join-link/${joinToken}/signup`,
            data: {
              firebase_token: firebaseToken,
              first_name: inviteDetails.first_name,
              last_name: inviteDetails.last_name
            }
          })
        } else if (inviteType === 'map' && inviteCode && inviteDetails) {
          // Map invites use their own endpoint, kept separate from project/org invites.
          response = await API({
            method: 'POST',
            url: `${HOST}/map-invite/${inviteCode}/accept`,
            data: {
              firebase_token: firebaseToken,
              first_name: inviteDetails.first_name,
              last_name: inviteDetails.last_name,
              email: inviteDetails.email || null,
              phone: inviteDetails.phone || null
            }
          })
        } else if (inviteCode && inviteDetails) {
          response = await API({
            method: 'POST',
            url: `${HOST}/token/invite/`,
            data: {
              firebase_token: firebaseToken,
              invite_code: inviteCode,
              first_name: inviteDetails.first_name,
              last_name: inviteDetails.last_name,
              email: inviteDetails.email || null,
              phone: inviteDetails.phone || null
            }
          })
        } else {
          // Regular login
          response = await API({
            method: 'POST',
            url: `${HOST}/token/`,
            data: {
              firebase_token: firebaseToken,
              invite_code: inviteCode
            }
          })
        }

        return response.data
      } catch (error: any) {
        // Check for USER_NOT_REGISTERED error
        if (error.response?.status === 403 && error.response?.data?.detail?.error === 'USER_NOT_REGISTERED') {
          console.log('[getAuthTokens] User not registered')
          // Return null to indicate user not registered
          return null
        }
        // Check for EMAIL_NOT_VERIFIED error
        if (error.response?.status === 403 && error.response?.data?.detail?.error === 'EMAIL_NOT_VERIFIED') {
          console.log('[getAuthTokens] Email not verified')
          // Return a special object to indicate email not verified
          return { emailNotVerified: true, message: error.response.data.detail.message }
        }
        throw error
      }
    },

    async loadInitialData() {
      try {
        await useOrgStore().fetchOrgs()
        const projectStore = useProjectStore()
        await Promise.all([
          projectStore.fetchProjects(),
          projectStore.fetchAllMembers(),
          projectStore.fetchAllTags(),
          projectStore.fetchAllInvites(),
        ])
        return true
      } catch (error) {
        console.error('Error loading initial data:', error)
        throw error
      }
    },
  },
})
